import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle, PaperPlaneTilt, Signpost, XCircle } from "@phosphor-icons/react";
import api from "@/lib/api";

const TONE = {
  ok: "border-emerald-700 bg-emerald-50 text-emerald-900",
  warn: "border-amber-600 bg-amber-50 text-amber-900",
  fail: "border-red-600 bg-red-50 text-red-900",
  info: "border-zinc-500 bg-zinc-50 text-zinc-800",
};
const SHOWN = 6;
const label = (b) => `${b.company ? `${b.company} · ` : ""}${b.name}${b.sent ? " (already sent)" : ""}${b.takes || b.test_only ? "" : " (doesn't match)"}`;
const takersOf = (d, asTest, anyway) => d.buyers.filter((b) => (asTest ? b.takes_test : b.takes || (anyway && !b.test_only)));
const passingOf = (d) => Boolean(d.can_pass && d.can_force);

function BuyerRow({ b }) {
  const Icon = b.takes ? CheckCircle : XCircle;
  return (
    <li className="flex items-start gap-2 px-3 py-2 text-xs" data-testid={`delivery-why-buyer-${b.id}`}>
      <Icon size={14} weight="fill" className={`mt-0.5 shrink-0 ${b.takes ? "text-emerald-700" : "text-red-600"}`} />
      <div className="min-w-0">
        <div className="font-bold break-words">
          {b.company ? `${b.company} · ` : ""}{b.name}
          {b.suggested && <span className="ml-1 border border-black bg-black px-1 text-[9px] uppercase tracking-widest text-white">This page&apos;s buyer</span>}
          {b.sent && <span className="ml-1 border border-emerald-700 px-1 text-[9px] uppercase tracking-widest text-emerald-800">Already sent</span>}
        </div>
        <div className="text-zinc-600">{b.takes ? "Would take this lead" : `Won't take it: ${b.reasons.join("; ")}`}</div>
      </div>
    </li>
  );
}

function BuyerList({ buyers }) {
  const [all, setAll] = useState(false);
  if (!buyers.length) return null;
  const shown = all ? buyers : buyers.slice(0, SHOWN);
  return (
    <div>
      <ul className="border-2 border-black divide-y divide-zinc-200 bg-white" data-testid="delivery-why-buyers">
        {shown.map((b) => <BuyerRow key={b.id} b={b} />)}
      </ul>
      {buyers.length > SHOWN && (
        <button type="button" onClick={() => setAll(!all)} data-testid="delivery-why-toggle-all"
                className="mt-1 text-[11px] underline">
          {all ? "Show fewer" : `Show all ${buyers.length} buyers`}
        </button>
      )}
    </div>
  );
}

function TestNote({ d }) {
  return (
    <div className="border-2 border-yellow-500 bg-yellow-50 px-3 py-2 text-[11px] leading-relaxed" data-testid="delivery-why-test-note">
      It goes out just like this buyer&apos;s real leads: their fields and formats, their email or web post, and their
      extra places, labelled <b>TEST</b>. It doesn&apos;t count toward their spend, lead count, caps or invoices, it isn&apos;t
      retried, and it&apos;s never passed to another buyer.
      {!d.is_test && !d.real_sent && <> This lead becomes a test lead.</>}
      {d.real_sent && <> This lead stays a real lead for the buyer who already got it.</>}
    </div>
  );
}

function TestResult({ r }) {
  return (
    <ul className="border border-black divide-y divide-zinc-200 bg-white text-xs" data-testid="delivery-why-test-result">
      {r.places.map((p, i) => (
        <li key={`${p.name}-${i}`} className="flex flex-wrap items-center gap-2 px-3 py-1.5" data-testid={`delivery-why-test-place-${i}`}>
          <b>{p.name}</b><span className="label-mono">{p.type}{p.extra_place ? " · extra place" : ""}</span>
          <span className={p.status === "delivered" ? "text-emerald-700 font-bold" : "text-red-700 font-bold"}>
            {p.status === "delivered" ? "Got it" : `Didn't go through${p.rejected ? `: ${p.rejected}` : ""}`}
          </span>
          {p.code && <span className="font-mono text-[10px] text-zinc-500">code {p.code}</span>}
          {p.fields_ok === false && <span className="text-amber-800">some of their fields didn&apos;t match, see Delivery attempts</span>}
        </li>
      ))}
    </ul>
  );
}

function Billing({ b, st }) {
  const where = st ? `${st} leads` : "leads from this state";
  return b.state_fee != null ? (
    <div data-testid="delivery-why-force-billing">It&apos;s a real lead: they&apos;re charged <b>${Number(b.state_fee).toFixed(2)}</b> for it, like any of their {where}, and it counts toward their lead count and caps.</div>
  ) : (
    <div className="font-bold" data-testid="delivery-why-force-billing">They have no price for {where}, so it counts toward their lead count but shows as unbilled on their invoice until you add a {st || "state"} price to their delivery.</div>
  );
}

function ForceNote({ b, st, qc, reason, setReason }) {
  return (
    <div className="border-2 border-red-600 bg-red-50 px-3 py-2 text-[11px] leading-relaxed space-y-1.5" data-testid="delivery-why-force-warning">
      {qc && (
        <div data-testid="delivery-why-pass-note">
          The quality check turned this lead away{qc.length ? `: ${qc.join("; ")}` : ""}. Sending it marks it as <b>passed</b>:
          it becomes a normal checked lead, and the traffic source that sent it is paid for it.
        </div>
      )}
      {!b.takes && <div><b>{b.company ? `${b.company} · ` : ""}{b.name}</b> doesn&apos;t take this lead: {b.reasons.join("; ")}.</div>}
      <Billing b={b} st={st} />
      <div>It goes out right away, even outside their lead hours or over their cap, and it&apos;s noted on this lead&apos;s history.</div>
      <input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} data-testid="delivery-why-force-reason"
             placeholder="Why are you sending it anyway? (required)"
             className="border border-black bg-white px-2 py-1 text-xs w-full sm:w-[28rem] max-w-full" />
    </div>
  );
}

function AnywayToggle({ anyway, setAnyway }) {
  return (
    <label className="flex items-center gap-2 text-xs cursor-pointer w-fit">
      <input type="checkbox" checked={anyway} onChange={(e) => setAnyway(e.target.checked)}
             className="accent-red-600" data-testid="delivery-why-anyway" />
      <b>Show buyers it doesn&apos;t match</b><span className="text-zinc-600">(send anyway)</span>
    </label>
  );
}

function SendBox({ d, pick, setPick, busy, onSend, asTest, setAsTest, anyway, setAnyway, reason, setReason }) {
  const testOnly = !d.can_send;
  const passing = passingOf(d);
  const takers = takersOf(d, asTest, anyway || passing);
  const resend = d.mode === "resend";
  const picked = takers.find((b) => b.id === pick);
  const forced = !asTest && picked && (passing || !picked.takes);
  const canForce = !passing && d.can_force && !asTest && d.buyers.some((b) => !b.takes && !b.test_only);
  const verb = passing ? "Pass and send to this buyer" : forced ? "Send anyway to this buyer" : resend ? "Resend to this buyer" : "Send to this buyer";
  return (
    <div className="space-y-2" data-testid="delivery-why-send-box">
      {!passing && (
        <label className="flex items-center gap-2 text-xs cursor-pointer w-fit">
          <input type="checkbox" checked={asTest} disabled={testOnly} onChange={(e) => setAsTest(e.target.checked)}
                 className="accent-black" data-testid="delivery-why-as-test" />
          <b>Send as test</b>{testOnly && <span className="text-zinc-600">(this lead can only go out as a test)</span>}
        </label>
      )}
      {asTest && <TestNote d={d} />}
      {canForce && <AnywayToggle anyway={anyway} setAnyway={setAnyway} />}
      {!takers.length ? (
        <span className="text-xs text-zinc-600" data-testid="delivery-why-no-takers">No active buyer takes this lead right now.</span>
      ) : (
        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          <select value={pick} onChange={(e) => setPick(e.target.value)} data-testid="delivery-why-pick"
                  className="border border-black px-2 py-1.5 text-xs w-full sm:w-[28rem] max-w-full">
            {!picked && <option value="">Pick a buyer…</option>}
            {takers.map((b) => <option key={b.id} value={b.id}>{label(b)}</option>)}
          </select>
          <button type="button" onClick={onSend} disabled={!picked || busy || (forced && reason.trim().length < 3)} data-testid="delivery-why-send"
                  className={`inline-flex items-center justify-center gap-1 border-2 px-3 py-1.5 text-[11px] uppercase tracking-widest transition-colors duration-100 disabled:opacity-50 ${asTest ? "border-yellow-500 bg-yellow-400 text-black hover:bg-yellow-300" : forced ? "border-red-700 bg-red-600 text-white hover:bg-red-700" : "border-black bg-black text-white hover:bg-zinc-800"}`}>
            <PaperPlaneTilt size={12} weight="bold" /> {busy ? "Sending…" : asTest ? "Send test to this buyer" : verb}
          </button>
        </div>
      )}
      {forced && <ForceNote b={picked} st={d.lead_state} qc={passing ? d.qc_reasons : null} reason={reason} setReason={setReason} />}
      {d.listed && !asTest && (
        <div className="text-[11px] text-zinc-600" data-testid="delivery-why-listed-note">
          It&apos;s on the marketplace now. Sending it to a buyer takes it off the marketplace.
        </div>
      )}
    </div>
  );
}

// Why a lead did or didn't reach a buyer, plus sending or resending it to one buyer.
export function DeliveryWhy({ leadId, onChanged }) {
  const [d, setD] = useState(null);
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState(false);
  const [asTest, setAsTest] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [anyway, setAnyway] = useState(false);
  const [reason, setReason] = useState("");

  const load = useCallback(() => api.get(`/leads/${leadId}/delivery-why`).then((r) => {
    setD(r.data);
    if (!r.data.can_send && !passingOf(r.data)) setAsTest(true);
  }).catch(() => setD(null)), [leadId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!d) return;
    const takers = takersOf(d, asTest, anyway || passingOf(d));
    const best = takers.find((b) => b.suggested && b.takes) || takers.find((b) => b.sent) || takers.find((b) => b.takes) || takers[0];
    // Pass mode overrides the quality check, so the admin must pick the buyer themselves.
    setPick((cur) => (takers.some((b) => b.id === cur) ? cur : best && !passingOf(d) ? best.id : ""));
  }, [d, asTest, anyway]);
  if (!d) return null;

  const sendTest = async () => {
    const b = d.buyers.find((x) => x.id === pick);
    if (!window.confirm(`Send this lead to ${label(b)} as a TEST?`)) return;
    setBusy(true);
    try {
      const r = await api.post(`/leads/${leadId}/send-test-to-buyer`, { destination_id: pick });
      setTestResult(r.data);
      const all = r.data.places.length;
      (r.data.delivered === all ? toast.success : toast.warning)(`Test sent to ${r.data.destination}: ${r.data.delivered} of ${all} place${all === 1 ? "" : "s"} got it`);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send the test");
    } finally {
      setBusy(false);
    }
  };

  const send = async () => {
    if (asTest) return sendTest();
    const b = d.buyers.find((x) => x.id === pick);
    const passing = passingOf(d);
    const forced = Boolean(b && (passing || !b.takes));
    const price = b?.state_fee != null ? ` They're charged $${Number(b.state_fee).toFixed(2)} for it.` : " They have no price for this state, so it shows as unbilled.";
    const ask = passing
      ? `Pass this lead and send it to ${label(b)}? The quality check turned it away${d.qc_reasons.length ? ` (${d.qc_reasons.join("; ")})` : ""}. It becomes a normal checked lead and the traffic source is paid for it.${price}`
      : `Send this lead to ${label(b)} anyway? It doesn't meet: ${b?.reasons.join("; ")}.${price}`;
    if (forced && !window.confirm(ask)) return;
    if (!forced && b?.sent && !window.confirm(`${label(b)} already got this lead. Send it to them again?`)) return;
    setBusy(true);
    try {
      const body = forced ? { destination_id: pick, force: true, reason: reason.trim() } : { destination_id: pick };
      const r = await api.post(`/leads/${leadId}/send-to-buyer`, body);
      const ok = r.data.delivery_status === "delivered";
      const verb = passing ? "Passed and sent" : forced ? "Sent anyway" : r.data.resent ? "Resent" : "Sent";
      (ok ? toast.success : toast.warning)(ok ? `${verb} to ${r.data.destination}` : `${verb} to ${r.data.destination}, but it didn't go through yet. It will retry.`);
      if (forced) { setAnyway(false); setReason(""); }
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send it");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="brut-card mt-4 p-5 space-y-3" data-testid="delivery-why">
      <div className="font-chivo font-bold uppercase tracking-tight flex items-center gap-2">
        <Signpost size={16} weight="bold" /> Where this lead went
      </div>
      <div className={`border-2 px-3 py-2 text-sm ${TONE[d.stage.tone] || TONE.info}`} data-testid="delivery-why-stage">
        {d.stage.text}
        {d.stage.href && <> <Link to={d.stage.href} className="font-bold underline" data-testid="delivery-why-stage-link">Open</Link></>}
      </div>
      {d.page && (
        <div className="text-xs" data-testid="delivery-why-page">
          Came from <b>{d.page.name}</b>{d.page.firm ? ` (${d.page.firm})` : ""}.{" "}
          <Link to={d.page.href} className="underline" data-testid="delivery-why-page-link">Open page</Link>
        </div>
      )}
      <BuyerList buyers={d.buyers} />
      {(d.can_send || d.can_test || passingOf(d)) && <SendBox d={d} pick={pick} setPick={setPick} busy={busy} onSend={send} asTest={asTest} setAsTest={setAsTest}
                                               anyway={anyway} setAnyway={setAnyway} reason={reason} setReason={setReason} />}
      {testResult && <TestResult r={testResult} />}
    </div>
  );
}
